import { useQueryClient } from "@tanstack/react-query";
import { useState, useTransition } from "react";
import { useTranslations } from "@/lib/useTranslations";
import { useNavigate } from "react-router-dom";

import { createEventAction } from "@/api/events-library";
import { Button } from "@/components/ui/Button";

function CreateSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

export function EventsLibraryCreateButton() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useTranslations("events.library");
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  function handleCreate() {
    setError(null);

    startTransition(async () => {
      try {
        const result = await createEventAction();
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["events-library-list"] }),
          queryClient.invalidateQueries({ queryKey: ["events-library-stats"] }),
          queryClient.invalidateQueries({
            queryKey: ["events-library-tab-counts"],
          }),
        ]);
        navigate(result.redirectTo);
      } catch (caughtError) {
        console.error("[EventsLibraryCreateButton]", caughtError);
        const message =
          caughtError instanceof Error ? caughtError.message : t("createFailed");
        setError(message);
      }
    });
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <Button
        type="button"
        disabled={isPending}
        aria-busy={isPending}
        onClick={handleCreate}
        className="inline-flex shrink-0 items-center justify-center gap-2"
      >
        {isPending ? <CreateSpinner /> : null}
        {isPending ? t("creating") : t("createEvent")}
      </Button>
      {error ? (
        <p
          role="alert"
          className="max-w-sm text-right text-xs text-whart-urgent"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
